import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { machineApi } from '@/api/machine';
import { toast } from 'sonner';
import { AlertCircle, CheckCircle, RefreshCw, ChevronDown, ChevronRight } from 'lucide-react';

const ValidationPanel = ({ machineId, validation, onRevalidate, compact = false }) => {
  const [validating, setValidating] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    overview: true,
    errors: true,
    warnings: true,
    capabilities: true,
    states: true
  });

  const handleRevalidate = async () => {
    setValidating(true);
    try {
      await onRevalidate();
      toast.success('Validation complete');
    } catch (error) {
      toast.error('Validation failed');
    } finally {
      setValidating(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (!validation) {
    return (
      <div className="text-center py-8 text-gray-400">
        <img src="/firmastate-icon.png" alt="" className="w-8 h-8 mx-auto mb-3 opacity-40" />
        <p className="text-sm">No validation results yet</p>
        <Button onClick={handleRevalidate} className="mt-3" size="sm" disabled={validating}>
          <RefreshCw className={`w-3 h-3 mr-2 ${validating ? 'animate-spin' : ''}`} />
          Run Validation
        </Button>
      </div>
    );
  }

  // Categorize issues
  const errors = validation.issues?.filter(i => i.severity === 'error') || [];
  const warnings = validation.issues?.filter(i => i.severity === 'warning') || [];

  // Group issues by category
  const issuesByCategory = {};
  validation.issues?.forEach(issue => {
    const category = issue.code.split('_')[0]; // e.g., MISSING, INVALID, UNREACHABLE
    if (!issuesByCategory[category]) {
      issuesByCategory[category] = [];
    }
    issuesByCategory[category].push(issue);
  });

  const SectionHeader = ({ section, title, count, icon: Icon }) => (
    <button
      onClick={() => toggleSection(section)}
      className="w-full flex items-center justify-between p-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded transition-colors"
      data-testid={`section-${section}`}
    >
      <div className="flex items-center space-x-3">
        {expandedSections[section] ? (
          <ChevronDown className="w-4 h-4 text-gray-500" />
        ) : (
          <ChevronRight className="w-4 h-4 text-gray-500" />
        )}
        {Icon && <Icon className="w-4 h-4" />}
        <span className="font-semibold text-sm">{title}</span>
        {count !== undefined && (
          <Badge variant="outline" className="text-[10px] h-4 px-1.5">
            {count}
          </Badge>
        )}
      </div>
    </button>
  );

  return (
    <div className="space-y-3" data-testid="validation-panel-full">
      {/* Header with Status */}
      <div className="flex items-center justify-between p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-white/10 rounded-lg">
        <div className="flex items-center space-x-3">
          {validation.valid ? (
            <>
              <div className="w-10 h-10 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <div className="font-bold text-green-400 text-lg">Valid Machine</div>
                <div className="text-xs text-gray-400">All validation checks passed</div>
              </div>
            </>
          ) : (
            <>
              <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-400" />
              </div>
              <div>
                <div className="font-bold text-red-400 text-lg">Invalid Machine</div>
                <div className="text-xs text-gray-400">
                  {errors.length} error{errors.length !== 1 ? 's' : ''}, {warnings.length} warning{warnings.length !== 1 ? 's' : ''}
                </div>
              </div>
            </>
          )}
        </div>
        <Button
          onClick={handleRevalidate}
          size="sm"
          variant="ghost"
          disabled={validating}
          data-testid="revalidate-button"
          className="h-8"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${validating ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      <ScrollArea className="h-[calc(100vh-250px)]">
        <div className="space-y-3">
          {/* Summary Statistics */}
          <div>
            <SectionHeader 
              section="overview" 
              title="Validation Summary" 
              icon={CheckCircle}
            />
            {expandedSections.overview && (
              <div className="mt-2 p-4 bg-black/30 border border-white/10 rounded space-y-3">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="p-3 bg-white/5 rounded">
                    <div className="text-xs text-gray-500 mb-1">Status</div>
                    <div className={`font-semibold ${validation.valid ? 'text-green-400' : 'text-red-400'}`}>
                      {validation.valid ? 'VALID' : 'INVALID'}
                    </div>
                  </div>
                  <div className="p-3 bg-white/5 rounded">
                    <div className="text-xs text-gray-500 mb-1">Total Issues</div>
                    <div className="font-semibold">{validation.issues?.length || 0}</div>
                  </div>
                  <div className="p-3 bg-red-500/10 border border-red-500/30 rounded">
                    <div className="text-xs text-gray-500 mb-1">Errors</div>
                    <div className="font-semibold text-red-400">{errors.length}</div>
                  </div>
                  <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded">
                    <div className="text-xs text-gray-500 mb-1">Warnings</div>
                    <div className="font-semibold text-yellow-400">{warnings.length}</div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Errors Section */}
          {errors.length > 0 && (
            <div>
              <SectionHeader 
                section="errors" 
                title="Errors" 
                count={errors.length}
                icon={AlertCircle}
              />
              {expandedSections.errors && (
                <div className="mt-2 space-y-2">
                  {errors.map((issue, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-red-500/10 border border-red-500/30 rounded"
                      data-testid={`error-${idx}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <Badge variant="destructive" className="text-[10px] h-4 px-1.5">
                          ERROR
                        </Badge>
                        <code className="text-[10px] text-gray-500 font-mono">{issue.code}</code>
                      </div>
                      <p className="text-sm leading-relaxed mb-2">{issue.message}</p>
                      {issue.location && Object.keys(issue.location).length > 0 && (
                        <div className="text-[10px] text-gray-500 font-mono bg-black/30 px-2 py-1 rounded">
                          {Object.entries(issue.location).map(([key, value]) => (
                            <div key={key}>
                              <span className="text-gray-600">{key}:</span> {value}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Warnings Section */}
          {warnings.length > 0 && (
            <div>
              <SectionHeader 
                section="warnings" 
                title="Warnings" 
                count={warnings.length}
                icon={AlertCircle}
              />
              {expandedSections.warnings && (
                <div className="mt-2 space-y-2">
                  {warnings.map((issue, idx) => (
                    <div
                      key={idx}
                      className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded"
                      data-testid={`warning-${idx}`}
                    >
                      <div className="flex items-start justify-between mb-2">
                        <Badge className="text-[10px] h-4 px-1.5 bg-yellow-500/20 text-yellow-400 border-yellow-500/30">
                          WARNING
                        </Badge>
                        <code className="text-[10px] text-gray-500 font-mono">{issue.code}</code>
                      </div>
                      <p className="text-sm leading-relaxed mb-2">{issue.message}</p>
                      {issue.location && Object.keys(issue.location).length > 0 && (
                        <div className="text-[10px] text-gray-500 font-mono bg-black/30 px-2 py-1 rounded">
                          {Object.entries(issue.location).map(([key, value]) => (
                            <div key={key}>
                              <span className="text-gray-600">{key}:</span> {value}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Issues by Category */}
          {Object.keys(issuesByCategory).length > 0 && (
            <div>
              <SectionHeader 
                section="categories" 
                title="Issues by Category" 
                count={Object.keys(issuesByCategory).length}
              />
              {expandedSections.categories && (
                <div className="mt-2 space-y-2">
                  {Object.entries(issuesByCategory).map(([category, issues]) => (
                    <div key={category} className="p-3 bg-white/5 border border-white/10 rounded">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-mono text-sm font-semibold text-blue-400">
                          {category}
                        </span>
                        <Badge variant="outline" className="text-[10px] h-4 px-1.5">
                          {issues.length}
                        </Badge>
                      </div>
                      <div className="space-y-1">
                        {issues.map((issue, idx) => (
                          <div key={idx} className="text-xs text-gray-400">
                            • {issue.message}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Success State */}
          {validation.valid && validation.issues?.length === 0 && (
            <div className="text-center py-8">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-400" />
              </div>
              <p className="text-lg font-semibold text-green-400">Perfect!</p>
              <p className="text-sm text-gray-400 mt-2">
                This machine has no validation issues.
              </p>
              <div className="mt-6 p-4 bg-green-500/10 border border-green-500/30 rounded-lg max-w-md mx-auto">
                <div className="text-xs text-gray-400 space-y-1">
                  <div>✓ All states are reachable</div>
                  <div>✓ No deadlocks detected</div>
                  <div>✓ All transitions are valid</div>
                  <div>✓ All effects and guards are defined</div>
                  <div>✓ Capabilities are correctly declared</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
};

export default ValidationPanel;
